import { useAuth } from "@hooks";
import { ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";
import { message, Spin } from "antd";
import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export const MagicLogin = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { verifyAccessLink, setAuth } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (verificationAttempted.current) {
      return;
    }

    if (!token) {
      message.error("No access token found");
      setStatus("error");
      return;
    }

    const verifyToken = async () => {
      try {
        verificationAttempted.current = true;
        const response = await verifyAccessLink.mutateAsync(token);
        setAuth(response.access_token);
        setStatus("success");

        // Redirect to home after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } catch (error) {
        const defaultMessage = "Invalid or expired access link";

        if (isAxiosError(error)) {
          message.error(error.response?.data.message || defaultMessage);
        } else {
          message.error(defaultMessage);
        }

        setStatus("error");
      }
    };

    verifyToken();

    // Failsafe: if verification takes too long, show error
    const timeoutId = setTimeout(() => {
      if (status === "loading") {
        setStatus("error");
        message.error("Verification timeout. Please try again.");
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [token]);

  return (
    <div className="flex min-h-screen bg-[url(/assets/images/bg-ruppert-ready-to-start.png)] bg-cover justify-center items-center">
      <div className="p-8 bg-white rounded-none shadow-md max-w-md w-full">
        {status === "loading" && (
          <div className="text-center py-8">
            <Spin size="large" />
            <p className="text-xl font-android font-semibold pt-8 text-black">VERIFYING YOUR ACCESS LINK...</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center py-6">
            <div className="flex justify-center mb-4">
              <CheckCircle size={48} weight="thin" className="text-yellow-principal" />
            </div>
            <h2 className="text-xl font-android font-semibold mb-2">AUTHENTICATION SUCCESSFUL</h2>
            <p className="text-gray-600 mb-6 font-barlow">
              You are being redirected to the dashboard...
            </p>
            <div className="flex justify-center">
              <ArrowRight size={24} className="text-yellow-principal animate-pulse" />
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-6">
            <div className="flex justify-center mb-4">
              <Warning size={48} weight="thin" className="text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Authentication failed</h2>
            <p className="text-gray-600 mb-6">
              Your access link may be expired or invalid.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="py-2 px-4 bg-primary text-black rounded-none hover:bg-primary/90 transition-colors"
            >
              Return to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
